import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IndianRupee, Inbox, Package, Users, ClipboardCheck, FileText, TrendingUp, AlertTriangle, ClipboardList, Phone, MessageCircle, Eye, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/pages/Portal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const TYPE_LABEL = { contact: "Contact", quote: "Quote", site_visit: "Site Visit", career: "Career" };
const waLinkFor = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  const full = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${full}?text=${encodeURIComponent("Hello, this is SecureFlame Fire Systems regarding your recent enquiry.")}`;
};

function Stat({ icon: Icon, label, value, accent, i }) {
  return (
    <div className="bg-card border border-border rounded-sm p-6 hover:-translate-y-1 transition-transform duration-200 animate-fade-up" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</span>
        <Icon size={20} className={accent ? "text-primary" : "text-secondary"} />
      </div>
      <p className="font-heading font-black text-3xl text-secondary">{value}</p>
    </div>
  );
}

function RecentLeadsWidget() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/leads/summary").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;

  const counters = [
    { v: "overdue", label: "Overdue", count: data.overdue_count, color: "text-red-600" },
    { v: "due_today", label: "Due Today", count: data.due_today_count, color: "text-amber-600" },
    { v: "upcoming", label: "Upcoming", count: data.upcoming_count, color: "text-secondary" },
  ];

  return (
    <div className="bg-card border border-border rounded-sm p-6 mt-8" data-testid="recent-leads-widget">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <h2 className="font-heading font-bold text-xl text-secondary">Recent Leads</h2>
          <Badge className="rounded-sm bg-primary/10 text-primary gap-1" data-testid="new-leads-count"><Sparkles size={11} /> New Leads: {data.new_count}</Badge>
        </div>
        <Link to="/portal/leads" data-testid="view-all-leads-link"><Button size="sm" variant="outline" className="rounded-sm">View All Leads</Button></Link>
      </div>

      <div className="flex flex-wrap gap-4 mb-5">
        {counters.map((c) => (
          <Link key={c.v} to={`/portal/leads?followup=${c.v}`} className="flex items-center gap-1.5 text-sm hover:underline" data-testid={`followup-counter-${c.v}`}>
            <span className={`font-heading font-bold ${c.color}`}>{c.count}</span>
            <span className="text-muted-foreground">{c.label}</span>
          </Link>
        ))}
      </div>

      <div className="space-y-2">
        {data.recent.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-dashed border-border pb-2 last:border-0" data-testid={`recent-lead-${l.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-secondary text-sm">{l.name}</span>
                {l.organization && <span className="text-xs text-muted-foreground">{l.organization}</span>}
                <Badge variant="outline" className="rounded-sm text-[10px]">{TYPE_LABEL[l.type]}</Badge>
                <Badge className="rounded-sm text-[10px] bg-amber-100 text-amber-700 capitalize">{l.status}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">{l.phone} · {new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Link to="/portal/leads"><Button size="sm" variant="ghost" className="rounded-sm h-8 px-2" data-testid={`recent-lead-view-${l.id}`}><Eye size={14} /></Button></Link>
              <a href={`tel:${l.phone}`}><Button size="sm" variant="ghost" className="rounded-sm h-8 px-2" data-testid={`recent-lead-call-${l.id}`}><Phone size={14} /></Button></a>
              <a href={waLinkFor(l.phone)} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="rounded-sm h-8 px-2" data-testid={`recent-lead-whatsapp-${l.id}`}><MessageCircle size={14} /></Button></a>
            </div>
          </div>
        ))}
        {data.recent.length === 0 && <p className="text-sm text-muted-foreground py-4">No leads yet.</p>}
      </div>
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const [s, setS] = useState({});

  useEffect(() => { api.get("/dashboard/stats").then((r) => setS(r.data)); }, []);

  const cards = {
    admin: [
      { icon: IndianRupee, label: "Revenue (Paid)", value: `₹${(s.total_revenue || 0).toLocaleString("en-IN")}`, accent: true },
      { icon: Inbox, label: "Total Enquiries", value: s.total_enquiries || 0 },
      { icon: TrendingUp, label: "New Enquiries", value: s.new_enquiries || 0, accent: true },
      { icon: Package, label: "Products", value: s.total_products || 0 },
      { icon: AlertTriangle, label: "Low Stock", value: s.low_stock || 0, accent: true },
      { icon: Users, label: "Clients", value: s.total_clients || 0 },
      { icon: ClipboardCheck, label: "Active Inspections", value: s.active_inspections || 0 },
      { icon: FileText, label: "Overdue Invoices", value: s.overdue_invoices || 0, accent: true },
    ],
    client: [
      { icon: Inbox, label: "My Enquiries", value: s.total_enquiries || 0 },
      { icon: ClipboardList, label: "Open Enquiries", value: s.open_enquiries || 0, accent: true },
      { icon: FileText, label: "Quotations", value: s.total_invoices || 0 },
      { icon: AlertTriangle, label: "Unpaid", value: s.pending_invoices || 0, accent: true },
    ],
    technician: [
      { icon: ClipboardCheck, label: "Total Inspections", value: s.total_inspections || 0 },
      { icon: TrendingUp, label: "Scheduled", value: s.scheduled || 0 },
      { icon: Package, label: "In Progress", value: s.in_progress || 0, accent: true },
      { icon: FileText, label: "Completed", value: s.completed || 0 },
    ],
  }[user.role] || [];

  return (
    <div className="p-8">
      <PageHeader title={`Welcome, ${user.name.split(" ")[0]}`} subtitle={`${user.role} dashboard`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => <Stat key={c.label} {...c} i={i} />)}
      </div>
      {user.role === "admin" && <RecentLeadsWidget />}
    </div>
  );
}

